'use client';

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type DropdownButtonProps = {
  buttonText?: string;
  icon?: LucideIcon;
  popUnderContent: ReactNode;
  className?: string;
  variant?: 'default' | 'black';
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

enum PopOverPosition {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
}

type PopOverProps = {
  position: PopOverPosition;
  left: number;
  top: number;
  children: ReactNode;
};

const PopOver = forwardRef<HTMLDivElement, PopOverProps>(
  ({ position, left, top, children }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'fixed z-[9999] w-max rounded-[12px] bg-white shadow-[0px_0px_2px_0px_rgba(31,32,44,0.04),0px_2px_10px_0px_rgba(31,32,44,0.10)] border border-[#ECEEF6]',
        position === PopOverPosition.Top ? 'mb-[6px]' : 'mt-[6px]',
      )}
      style={{
        minWidth: '200px',
        maxWidth: '320px',
        maxHeight: '50vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        left: `${left}px`,
        top: `${top}px`,
      }}
    >
      {children}
    </div>
  ),
);

PopOver.displayName = 'PopOver';

function calculatePopoverLeft(rect: DOMRect, popoverWidth: number): number {
  let left = rect.left + rect.width / 2 - popoverWidth / 2;
  left = Math.max(20, left);
  if (left + popoverWidth > window.innerWidth - 20) {
    left = window.innerWidth - popoverWidth - 20;
  }
  return left;
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
  buttonText,
  icon: Icon,
  popUnderContent,
  className,
  variant = 'default',
  isOpen: controlledIsOpen,
  onOpenChange,
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [position, setPosition] = useState(PopOverPosition.Bottom);
  const [popoverLeft, setPopoverLeft] = useState(0);
  const [popoverTop, setPopoverTop] = useState(0);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const popOverRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
  const setIsOpen = onOpenChange ?? setUncontrolledIsOpen;

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const popoverWidth = popOverRef.current?.offsetWidth || 200;
    const left = calculatePopoverLeft(rect, popoverWidth);

    setPopoverLeft(left);
    setPopoverTop(rect.bottom + 6);

    const timeoutId = setTimeout(() => {
      const popoverHeight = popOverRef.current?.getBoundingClientRect().height ?? 0;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < popoverHeight && spaceAbove > popoverHeight) {
        setPopoverTop(rect.top - popoverHeight - 6);
        setPosition(PopOverPosition.Top);
      } else {
        setPosition(PopOverPosition.Bottom);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      popOverRef.current
      && !popOverRef.current.contains(event.target as Node)
      && buttonRef.current
      && !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, [setIsOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={clsx(
          'cursor-pointer rounded-[4px] text-[15px] font-medium leading-[120%] text-center font-noiGrotesk tracking-[0px]',
          Icon ? 'w-[42px] h-[42px] flex items-center justify-center p-0' : 'px-[16px] py-[11px]',
          variant === 'default' && 'border border-[#BBBDD1] bg-[#FFF] text-[#000] hover:border-[#878AAE] hover:bg-[#F3F4FA]',
          variant === 'black' && 'bg-black text-white border-none hover:bg-[#333]',
          isOpen && variant === 'default' && 'bg-[#F3F4FA] border-[#878AAE]',
          isOpen && variant === 'black' && 'bg-[#222]',
          className,
        )}
        style={{ fontFeatureSettings: '\'ss03\' on' }}
      >
        {Icon ? <Icon className="size-5" /> : buttonText}
      </button>

      {isOpen && (
        <PopOver
          ref={popOverRef}
          position={position}
          left={popoverLeft}
          top={popoverTop}
        >
          {popUnderContent}
        </PopOver>
      )}
    </div>
  );
};

export { DropdownButton };
